import { logWithState } from '@gimlite/common/functions/console.function';
import { setCSS } from '@gimlite/common/functions/css.function';
import { action, makeAutoObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useStoreUIV2 } from '../hooks/useStore.hook';
import { ColorType } from '../types/component.type';
import { RootStoreMobX } from './root.store';

export type ThemeStoreContextType = {
  theme: 'dark' | 'light';
  defaultTheme: ThemeStoreContextType['theme'];
};

const colorMapper = {
  light: {
    deep_indigo: '#232653',
    deep_indigo_70: '#656787',
    deep_indigo_50: '#9192A2',
    deep_indigo_30: '#BDBECB',
    deep_indigo_10: '#E9E9EE',
    deep_indigo_4: '#F6F6F8',
    soft_teal: '#20C997',
    soft_teal_70: '#63D9B6',
    soft_teal_50: '#8FE4CB',
    soft_teal_30: '#BCEFE0',
    soft_teal_10: '#E9FAF5',
    black_90: '#232323',
    black_70: '#3F3F3F',
    black_50: '#7F7F7F',
    black_15: '#D8D8D8',
    black_4: '#F4F4F4',
    black_2: '#FAFAFA',
    white_100: '#ffffff',
    white_70: '#ffffff70',
    white_50: '#ffffff50',
    white_30: '#ffffff30',
    white_10: '#ffffff10',
    alert_success: '#28A745',
    alert_success_50: '#93D3A2',
    alert_success_20: '#D4EDDA',
    alert_warning: '#FD7E14',
    alert_warning_50: '#FEBE89',
    alert_warning_20: '#FFE5D0',
    alert_advisory: '#FFBF00',
    alert_advisory_50: '#FFDF80',
    alert_advisory_20: '#FFF3CC',
    alert_error: '#DC3545',
    alert_error_50: '#ED9AA2',
    alert_error_20: '#F8D7DA',
    alert_info: '#17A2B8',
    alert_info_50: '#8BD1DB',
    alert_info_20: '#D1F4FA',
  },
  dark: {
    deep_indigo: '#232653',
    deep_indigo_70: '#656787',
    deep_indigo_50: '#9192A2',
    deep_indigo_30: '#BDBECB',
    deep_indigo_10: '#E9E9EE',
    deep_indigo_4: '#F6F6F8',
    soft_teal: '#20C997',
    soft_teal_70: '#63D9B6',
    soft_teal_50: '#8FE4CB',
    soft_teal_30: '#BCEFE0',
    soft_teal_10: '#E9FAF5',
    black_90: '#232323',
    black_70: '#3F3F3F',
    black_50: '#7F7F7F',
    black_15: '#D8D8D8',
    black_4: '#F4F4F4',
    black_2: '#FAFAFA',
    alert_success: '#28A745',
    alert_success_50: '#93D3A2',
    alert_success_20: '#D4EDDA',
    alert_warning: '#FD7E14',
    alert_warning_50: '#FEBE89',
    alert_warning_20: '#FFE5D0',
    alert_advisory: '#FFBF00',
    alert_advisory_50: '#FFDF80',
    alert_advisory_20: '#FFF3CC',
    alert_error: '#DC3545',
    alert_error_50: '#ED9AA2',
    alert_error_20: '#F8D7DA',
    alert_info: '#17A2B8',
    alert_info_50: '#8BD1DB',
    white_100: '#ffffff',
    white_70: '#ffffff70',
    white_50: '#ffffff50',
    white_30: '#ffffff30',
    white_10: '#ffffff10',
    alert_info_20: '#D1F4FA',
  },
} as const satisfies Record<
  ThemeStoreContextType['theme'],
  Record<ColorType, string>
>;

const colors = Object.freeze({
  theme: {
    light: {
      ...colorMapper.light,
    },
    dark: {
      ...colorMapper.dark,
    },
  },
} as const);

export class ThemeStoreMobX {
  rootStore: RootStoreMobX;
  @observable public theme: ThemeStoreContextType['theme'] = 'light';
  @observable public registryColors = colors;
  @observable private colorsTheme = colors.theme[this.theme];
  @observable public load: boolean = false;

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @action public init({
    defaultTheme,
  }: Pick<ThemeStoreContextType, 'defaultTheme'>) {
    this.load = false;
    this.theme = defaultTheme;
    this.setTheme(this.theme);
    this.load = true;
  }

  @action public getTheme() {
    return localStorage.getItem('theme') as
      | ThemeStoreContextType['theme']
      | null;
  }

  @action public setTheme(defaultTheme: ThemeStoreContextType['theme']) {
    this.theme = defaultTheme;
    this.colorsTheme = colors.theme[this.theme];
    localStorage.setItem('theme', this.theme);
    this.setColorsTheme(this.theme);
    this.setColorsChart(this.theme);
  }

  @action private setColorsTheme(theme: ThemeStoreContextType['theme']) {
    Object.entries(this.colorsTheme).forEach(([key, value]) =>
      setCSS(`--color-${key}`, value as string),
    );
  }

  @action private setColorsChart(theme: ThemeStoreContextType['theme']) {
    // Chart.defaults.borderColor = this.colorsTheme.border;
  }
}

export const ConfigTheme = observer(
  ({
    children,
    defaultTheme,
  }: { children: React.ReactNode } & Pick<
    ThemeStoreContextType,
    'defaultTheme'
  >) => {
    const { ThemeStore } = useStoreUIV2();

    useEffect(() => {
      if (!ThemeStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigTheme init' });
        ThemeStore.init({ defaultTheme });
      }

      if (ThemeStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigTheme load' });
      }
    }, [ThemeStore.load]);

    return ThemeStore.load ? children : null;
  },
);
